import clsx from 'clsx';
import { m, LazyMotion, domAnimation, useAnimation } from 'framer-motion';
import { graphql, navigate, useStaticQuery, Link as GatsbyLink } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';

import DEFAULT_EASE from 'constants/default-ease';
import MainContext from 'context/main';

import ArrowIcon from './images/arrow.inline.svg';

const underlineStyles =
  'relative font-bold tracking-wide uppercase transition-colors duration-200 hover:text-primary-2';

const styles = {
  base: 'inline-flex items-center group leading-none font-sans2 relative after:absolute after:w-full after:h-1.5 after:inset-x-0 after:-bottom-1.5',
  size: {
    xl: 'text-[16px] md:text-[14px]',
    md: 'text-[16px] xl:text-[14px]',
    sm: 'text-[14px] md:text-[12px]',
  },
  theme: {
    black: 'text-new-black transition-colors duration-200 hover:text-primary-2',
    'black-underline': `text-black ${underlineStyles}`,
    'white-underline': `text-white ${underlineStyles}`,
    'primary-2':
      'text-primary-2 no-underline font-medium transition-colors duration-200 ease-in-out hover:text-secondary-2',
    secondary:
      'text-new-gray-1/80 after:hidden underline underline-offset-2 decoration-dotted transition-colors duration-200 ease-in-out hover:text-secondary-2',
    'secondary-2':
      'text-secondary-2 no-underline font-medium transition-colors duration-200 font-sans2 ease-in-out hover:text-blue-hover',
  },
};

const underlineVariants = {
  initial: {
    right: 0,
    left: 'auto',
    width: '100%',
    scaleX: -1,
  },
  start: {
    right: 0,
    left: 'auto',
    width: 0,
    scaleX: -1,
    transition: {
      duration: 0.25,
      ease: DEFAULT_EASE,
    },
    transitionEnd: {
      right: 'auto',
      left: 0,
      scaleX: 1,
    },
  },
  finish: {
    width: '100%',
    transition: {
      duration: 0.25,
      ease: DEFAULT_EASE,
    },
    transitionEnd: {
      right: 0,
      left: 'auto',
      scaleX: -1,
    },
  },
};

const Link = ({
  className: additionalClassName,
  size,
  theme,
  withArrow,
  to,
  children,
  includeBaseStyles,
  ...props
}) => {
  const [canAnimate, setCanAnimate] = useState(true);
  const controls = useAnimation();
  const { language } = useContext(MainContext);

  const {
    allSitePage: { nodes: pages },
  } = useStaticQuery(graphql`
    {
      allSitePage {
        nodes {
          path
        }
      }
    }
  `);

  const className = clsx(
    ((theme && size) || includeBaseStyles) && styles.base,
    styles.size[size],
    styles.theme[theme],
    additionalClassName
  );

  const isUnderline = theme === 'black-underline' || theme === 'white-underline';

  const handleHover = () => {
    if (!isUnderline) return;
    if (!canAnimate) return;

    setCanAnimate(false);

    controls.start('start').then(() => controls.start('finish').then(() => setCanAnimate(true)));
  };

  const underline = (
    <LazyMotion features={domAnimation}>
      <m.span
        className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded-full bg-primary-2"
        initial="initial"
        variants={underlineVariants}
        animate={controls}
        aria-hidden
      />
    </LazyMotion>
  );

  const arrow = (
    <ArrowIcon
      className="h-[10px] w-[7px] fill-secondary-2 group-hover:fill-blue-hover transition-colors duration-200 mt-px ml-1"
      aria-hidden
    />
  );

  const handleClick = (event, to) => {
    event.preventDefault();
    const locale = language?.locale === 'ja' ? `/jp` : '/';

    if (to.startsWith(locale)) navigate(to);

    const page = pages.find((p) => p.path === `${locale}${to}`);

    const targetPath = page ? `${locale}${to}` : to;
    navigate(targetPath);

    return null;
  };

  if (to.startsWith('/')) {
    return (
      <GatsbyLink
        className={className}
        to={to}
        onMouseEnter={handleHover}
        onTouchStart={handleHover}
        onClick={(event) => handleClick(event, to)}
        {...props}
      >
        {children}
        {withArrow && arrow}
        {isUnderline && underline}
      </GatsbyLink>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    <a
      className={className}
      href={to}
      onMouseEnter={isUnderline ? handleHover : undefined}
      {...props}
    >
      {children}
      {withArrow && arrow}
      {isUnderline && underline}
    </a>
  );
};

Link.propTypes = {
  className: PropTypes.string,
  to: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(styles.size)),
  theme: PropTypes.oneOf(Object.keys(styles.theme)),
  withArrow: PropTypes.bool,
  includeBaseStyles: PropTypes.bool,
  children: PropTypes.node,
};

Link.defaultProps = {
  className: null,
  to: null,
  size: null,
  theme: null,
  withArrow: false,
  includeBaseStyles: false,
  children: null,
};

export default Link;
